import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import type { UserCard } from '@/features/auth/types'
import { echo } from '@/shared/echo'
import { fetchFriends, fetchInvites, fetchRequests, type Invite } from './api'

export const friendsKey = ['friends'] as const
export const requestsKey = ['friends', 'requests'] as const
export const invitesKey = ['invites'] as const

/**
 * Друзья со статусом «в сети».
 *
 * Статус приходит отметкой времени последнего запроса (см. TouchLastSeen на
 * бэкенде), поэтому список подтягивается по таймеру: событие «ушёл» никто не
 * шлёт — человек просто закрывает вкладку.
 */
export function useFriends(enabled: boolean) {
  return useQuery<UserCard[]>({
    queryKey: friendsKey,
    queryFn: fetchFriends,
    enabled,
    refetchInterval: 30_000,
    staleTime: 15_000,
  })
}

export function useFriendRequests(enabled: boolean) {
  return useQuery({ queryKey: requestsKey, queryFn: fetchRequests, enabled })
}

export function useInvites(enabled: boolean) {
  return useQuery<Invite[]>({ queryKey: invitesKey, queryFn: fetchInvites, enabled })
}

/**
 * Свой канал: заявки в друзья и приглашения в партию.
 *
 * Канал приватный и один на человека — тот же, что заводит Laravel для
 * уведомлений. Событие несёт только отметку «изменилось»: списки приходят
 * запросом, чтобы у клиента не было второго, расходящегося источника правды.
 */
export function useUserChannel(userId: number | null): void {
  const client = useQueryClient()

  useEffect(() => {
    if (userId === null) return

    const name = `App.Models.User.${userId}`
    const channel = echo.private(name)

    channel.listen('.game.invited', () => {
      void client.invalidateQueries({ queryKey: invitesKey })
    })

    channel.listen('.friendship.changed', () => {
      void client.invalidateQueries({ queryKey: friendsKey })
      void client.invalidateQueries({ queryKey: requestsKey })
    })

    return () => {
      echo.leave(name)
    }
  }, [userId, client])
}
